import type { DataGridColumn } from '@/components/tables/DataGrid';
import { formatDateTime } from '@/utils/format';
import { ApprovalRequestCategory, type ApprovalRequestRowDto } from '@/api/types/approvalRequests';

// Render a dynamic `fields[key]` value as plain text (parity with legacy which just renders).
const fieldStr = (row: ApprovalRequestRowDto, key: string): string => {
  const v = row.fields?.[key];
  return v == null ? '' : String(v);
};
const fieldDate = (row: ApprovalRequestRowDto, key: string): string => {
  const v = row.fields?.[key];
  return v == null ? '' : formatDateTime(String(v));
};

// Status badge colouring (parity with Terminal history statusBadge): Approved=success,
// Declined/Rejected=danger, Pending=warning.
export function statusBadgeClass(status: string): string {
  const v = status.toLowerCase();
  if (v === 'approved') return 'text-bg-success';
  if (v === 'declined' || v === 'rejected') return 'text-bg-danger';
  return 'text-bg-warning';
}

// Port of PendingRequestColumnBuilder.Build(descriptors, history, includeActionType).
// includeActionType is derived from category: MatchingEngine (Match + Unmatch) = true,
// Aml (Provider Change only) = false. Requests-tab data columns are not orderable
// (legacy Orderable = history); history columns are all orderable + Status appended.
export function buildRequestColumns(
  category: ApprovalRequestCategory,
  history: boolean,
): DataGridColumn<ApprovalRequestRowDto>[] {
  const includeActionType = category === ApprovalRequestCategory.MatchingEngine;
  const sortable = history;
  const cls = 'text-start';

  const cols: DataGridColumn<ApprovalRequestRowDto>[] = [];

  cols.push({
    key: 'requestedBy',
    title: 'Requester',
    className: cls,
    sortable,
    value: (r) => r.requestedBy ?? '',
  });
  cols.push({
    key: 'requestedAt',
    title: 'Request Date',
    className: cls,
    sortable,
    filterable: false,
    value: (r) => r.requestedAt,
    cell: (r) => formatDateTime(r.requestedAt),
  });

  if (includeActionType) {
    cols.push({
      key: 'actionType',
      title: 'Action Type',
      className: cls,
      sortable,
      value: (r) => fieldStr(r, 'actionType'),
      cell: (r) => fieldStr(r, 'actionType'),
    });
  }

  cols.push({
    key: 'reason',
    title: 'Comment',
    className: cls,
    sortable,
    value: (r) => r.reason ?? '',
  });

  if (history) {
    cols.push({
      key: 'resolvedBy',
      title: 'Approver',
      className: cls,
      sortable,
      filterable: false,
      value: (r) => r.resolvedBy ?? '',
    });
    cols.push({
      key: 'resolvedAt',
      title: 'Approval Date',
      className: cls,
      sortable,
      filterable: false,
      value: (r) => r.resolvedAt,
      cell: (r) => formatDateTime(r.resolvedAt),
    });
    cols.push({
      key: 'resolutionComment',
      title: 'Response',
      className: cls,
      sortable,
      filterable: false,
      value: (r) => r.resolutionComment ?? '',
    });
  }

  // Per-type columns (union, de-duplicated by key — both Match/Unmatch share the same set).
  if (category === ApprovalRequestCategory.Aml) {
    cols.push(
      { key: 'ticket', title: 'Ticket Number', className: cls, sortable, value: (r) => fieldStr(r, 'ticket'), cell: (r) => fieldStr(r, 'ticket') },
      { key: 'login', title: 'Login', className: cls, sortable, value: (r) => fieldStr(r, 'login'), cell: (r) => fieldStr(r, 'login') },
      { key: 'uniqueId', title: 'Unique Id', className: cls, sortable, value: (r) => fieldStr(r, 'uniqueId'), cell: (r) => fieldStr(r, 'uniqueId') },
      { key: 'transactionDate', title: 'Transaction Date', className: cls, sortable, filterable: false, value: (r) => fieldStr(r, 'transactionDate'), cell: (r) => fieldDate(r, 'transactionDate') },
      { key: 'oldProvider', title: 'Previous Provider', className: cls, sortable, filterable: false, value: (r) => fieldStr(r, 'oldProvider'), cell: (r) => fieldStr(r, 'oldProvider') },
      { key: 'newProvider', title: 'New Provider', className: cls, sortable, filterable: false, value: (r) => fieldStr(r, 'newProvider'), cell: (r) => fieldStr(r, 'newProvider') },
    );
  } else {
    cols.push(
      { key: 'provider', title: 'Provider', className: cls, sortable, value: (r) => fieldStr(r, 'provider'), cell: (r) => fieldStr(r, 'provider') },
      { key: 'ticket', title: 'Ticket', className: cls, sortable, value: (r) => fieldStr(r, 'ticket'), cell: (r) => fieldStr(r, 'ticket') },
      { key: 'connectorId', title: 'Connector', className: cls, sortable, filterable: false, value: (r) => fieldStr(r, 'connectorId'), cell: (r) => fieldStr(r, 'connectorId') },
      { key: 'statementIds', title: 'Statement Ids', className: cls, sortable, value: (r) => fieldStr(r, 'statementIds'), cell: (r) => fieldStr(r, 'statementIds') },
    );
  }

  if (history) {
    cols.push({
      key: 'status',
      title: 'Status',
      className: cls,
      sortable,
      filterable: false,
      value: (r) => r.status,
    });
  }

  return cols;
}
